import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { inquiryAPI, Inquiry, InquiryStats } from '../../api';
import { useAuth } from '../../contexts/AuthContext';

// 스타일드 컴포넌트
const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.5rem;
  }
`;

const PageDescription = styled.p`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 0.5rem;
  }
`;

const StatCard = styled.div<{ type?: string }>`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 1;
  min-width: 120px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0.75rem;
    min-width: 90px;
  }
  
  ${({ type, theme }) => {
    switch (type) {
      case 'pending':
        return `
          border-left: 3px solid ${theme.colors.warning};
        `;
      case 'answered':
        return `
          border-left: 3px solid ${theme.colors.info};
        `;
      case 'closed':
        return `
          border-left: 3px solid ${theme.colors.success};
        `;
      default:
        return `
          border-left: 3px solid ${theme.colors.primary};
        `;
    }
  }}
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.2rem;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ active, theme }) => active ? theme.colors.primary : 'white'};
  color: ${({ active, theme }) => active ? 'white' : theme.colors.text};
  border-radius: 0.25rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.background};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: 1;
    font-size: 0.8rem;
    padding: 0.4rem 0.75rem;
  }
`;

const Button = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s;
  
  &:hover {
    color: white;
    opacity: 0.8;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    padding: 0.6rem 1rem;
  }
`;

const InquiryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const InquiryCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const InquiryCardLink = styled(Link)`
  display: block;
  padding: 1rem;
  text-decoration: none;
  color: inherit;
`;

const InquiryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const InquiryTitle = styled.h3`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1rem;
  }
`;

const InquiryMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    justify-content: space-between;
  }
`;

const StatusBadge = styled.span<{ status: 'pending' | 'answered' | 'closed' }>`
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  
  ${({ status, theme }) => {
    switch (status) {
      case 'pending':
        return `
          background-color: ${theme.colors.warning};
          color: black;
        `;
      case 'answered':
        return `
          background-color: ${theme.colors.info};
          color: black;
        `;
      case 'closed':
        return `
          background-color: ${theme.colors.success};
          color: black;
        `;
      default:
        return '';
    }
  }}
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }
`;

const DateText = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const InquiryContent = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 0.85rem;
  }
`;

const InquiryFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const ReplyCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ReplyIcon = styled.span`
  font-size: 1rem;
`;

const EmptyState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 1.5rem;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  gap: 0.5rem;
`;

const PageButton = styled.button<{ active?: boolean }>`
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ active, theme }) => active ? theme.colors.primary : 'white'};
  color: ${({ active, theme }) => active ? 'white' : theme.colors.text};
  border-radius: 0.25rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.background};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 2rem;
    height: 2rem;
    font-size: 0.8rem;
  }
`;

const Loading = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 1rem;
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  margin: 1rem 0;
  background-color: ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  border-radius: 0.25rem;
  font-size: 0.9rem;
`;

// 상태 라벨 매핑
const statusLabels: Record<string, string> = {
  pending: '답변 대기',
  answered: '답변 완료',
  closed: '해결 완료'
};

const InquiryListPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [stats, setStats] = useState<InquiryStats>({
    total: 0,
    pending: 0,
    answered: 0,
    closed: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // 로그인 확인
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/inquiry' } });
    }
  }, [isAuthenticated, navigate]);
  
  // 문의 목록 및 통계 데이터 로드
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 문의 목록 로드
        const inquiriesResponse = await inquiryAPI.getMyInquiries(page, 10, statusFilter);
        
        if (inquiriesResponse.data.success) {
          setInquiries(inquiriesResponse.data.inquiries || []);
          setTotalPages(inquiriesResponse.data.pagination.totalPages || 1);
        } else {
          throw new Error('문의 목록을 불러오는데 실패했습니다');
        }
        
        // 최초 로드 시에만 통계 데이터 가져오기
        if (page === 1 && statusFilter === 'all') {
          const statsResponse = await inquiryAPI.getMyInquiryStats();
          
          if (statsResponse.data.success) {
            setStats(statsResponse.data.stats || {
              total: 0,
              pending: 0,
              answered: 0,
              closed: 0
            });
          }
        }
      } catch (err) {
        console.error('문의 데이터 로드 실패:', err);
        setError('문의 데이터를 불러오는데 문제가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [page, statusFilter]);
  
  // 필터 변경 핸들러
  const handleFilterChange = (status: string) => {
    setStatusFilter(status);
    setPage(1);
  };
  
  // 날짜 포맷 함수
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // 페이지네이션 렌더링
  const renderPagination = () => {
    const pageButtons = [];
    const maxVisible = 5;
    
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    
    // 이전 버튼
    pageButtons.push(
      <PageButton 
        key="prev" 
        onClick={() => setPage(prev => Math.max(1, prev - 1))}
        disabled={page === 1}
      >
        &lt;
      </PageButton>
    );
    
    // 페이지 버튼들
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <PageButton 
          key={i} 
          active={i === page}
          onClick={() => setPage(i)}
        >
          {i}
        </PageButton>
      );
    }
    
    // 다음 버튼
    pageButtons.push(
      <PageButton 
        key="next" 
        onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
        disabled={page === totalPages || totalPages === 0}
      >
        &gt;
      </PageButton>
    );
    
    return pageButtons;
  };
  
  if (loading && inquiries.length === 0) {
    return (
      <Container>
        <PageHeader>
          <PageTitle>문의내역</PageTitle>
        </PageHeader>
        <Loading>문의 내역을 불러오는 중입니다...</Loading>
      </Container>
    );
  }
  
  return (
    <Container>
      <PageHeader>
        <PageTitle>문의내역</PageTitle>
        <PageDescription>문의 내역을 확인하고 새로운 문의를 작성하실 수 있습니다.</PageDescription>
      </PageHeader>
      
      <StatsContainer>
        <StatCard>
          <StatLabel>전체 문의</StatLabel>
          <StatValue>{stats.total || 0}</StatValue>
        </StatCard>
        <StatCard type="pending">
          <StatLabel>답변 대기</StatLabel>
          <StatValue>{stats.pending || 0}</StatValue>
        </StatCard>
        <StatCard type="answered">
          <StatLabel>답변 완료</StatLabel>
          <StatValue>{stats.answered || 0}</StatValue>
        </StatCard>
        <StatCard type="closed">
          <StatLabel>해결 완료</StatLabel>
          <StatValue>{stats.closed || 0}</StatValue>
        </StatCard>
      </StatsContainer>
      
      <ActionsContainer>
        <FilterContainer>
          <FilterButton 
            active={statusFilter === 'all'}
            onClick={() => handleFilterChange('all')}
          >
            전체
          </FilterButton>
          <FilterButton 
            active={statusFilter === 'pending'}
            onClick={() => handleFilterChange('pending')}
          >
            답변 대기
          </FilterButton>
          <FilterButton 
            active={statusFilter === 'answered'}
            onClick={() => handleFilterChange('answered')}
          >
            답변 완료
          </FilterButton>
          <FilterButton 
            active={statusFilter === 'closed'}
            onClick={() => handleFilterChange('closed')}
          >
            해결 완료
          </FilterButton>
        </FilterContainer>
        
        <Button to="/inquiry/new">
          새 문의 작성
        </Button>
      </ActionsContainer>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {!loading && inquiries.length === 0 ? (
        <EmptyState>
          <EmptyStateText>문의 내역이 없습니다.</EmptyStateText>
          <Button to="/inquiry/new">새 문의 작성하기</Button>
        </EmptyState>
      ) : (
        <>
          <InquiryList>
            {inquiries.map(inquiry => (
              <InquiryCard key={inquiry.inquiry_id}>
                <InquiryCardLink to={`/inquiry/${inquiry.inquiry_id}`}>
                  <InquiryHeader>
                    <InquiryTitle>{inquiry.title}</InquiryTitle>
                    <InquiryMeta>
                      <StatusBadge status={inquiry.status}>
                        {statusLabels[inquiry.status]}
                      </StatusBadge>
                      <DateText>{formatDate(inquiry.created_at)}</DateText>
                    </InquiryMeta>
                  </InquiryHeader>
                  <InquiryContent>{inquiry.content}</InquiryContent>
                  <InquiryFooter>
                    <ReplyCount>
                      <ReplyIcon>💬</ReplyIcon> 
                      답변 {inquiry.reply_count || 0}
                    </ReplyCount>
                    {inquiry.last_reply_at && (
                      <DateText>최근 답변: {formatDate(inquiry.last_reply_at)}</DateText>
                    )}
                  </InquiryFooter>
                </InquiryCardLink>
              </InquiryCard>
            ))}
          </InquiryList>
          
          {totalPages > 1 && (
            <Pagination>
              {renderPagination()}
            </Pagination>
          )}
        </>
      )}
    </Container>
  );
};

export default InquiryListPage; 